"use client";
import s from "@/components/shared/TabButtons.module.scss";
import cn from "classnames";
import { ReactNode, useCallback, useEffect, useRef } from "react";
import { debounce, upperFirst } from "lodash";

type TabButtonsProps<T extends string | number> = {
  items: {
    tabKey: T;
    button: ReactNode;
  }[];
  variant?: "default" | "light" | "reviews";
  activeKey: string | number;
  size?: "default" | "small";
  breakpoint?: "sm" | "md" | "none";
  fullWidth?: boolean;
  onChange: (value: T) => void;
  className?: string;
};

export const TabButtons = <T extends string | number>({
  items,
  activeKey,
  onChange,
  breakpoint = "sm",
  variant = "default",
  size = "default",
  className,
  fullWidth,
}: TabButtonsProps<T>) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const activeKeyRef = useRef(activeKey);
  activeKeyRef.current = activeKey;

  const watchScroller = useCallback(() => {
    if (!scrollerRef.current || !activeKeyRef.current) return;
    const parentEl = scrollerRef.current.parentElement;
    if (!parentEl) return;
    const activeIndex = items.findIndex(({ tabKey }) => tabKey === activeKeyRef.current);

    const buttons = parentEl.querySelectorAll("button");

    const targetBtn = buttons[activeIndex];
    if (!targetBtn) return;

    const { left: containerLeft } = parentEl.getBoundingClientRect();

    const { left } = targetBtn.getBoundingClientRect();

    scrollerRef.current.setAttribute(
      "style",
      `width: ${targetBtn.clientWidth - 8}px; transform: translateX(${
        left - containerLeft + parentEl.scrollLeft
      }px)`
    );
  }, [items]);

  useEffect(() => {
    watchScroller();
  }, [activeKey, watchScroller]);

  useEffect(() => {
    const watchFn = debounce(watchScroller, 10);
    window.addEventListener("resize", watchFn);

    return () => {
      window.removeEventListener("resize", watchFn);
    };
  }, [watchScroller]);

  return (
    <div
      className={cn(
        s.btnsWrap,
        s[`variant${upperFirst(variant)}`],
        s[`breakpoint${upperFirst(breakpoint)}`],
        s[`size${upperFirst(size)}`],
        { [s.fullWidth]: fullWidth },
        className
      )}
    >
      <div className={s.btns}>
        {items.map(({ tabKey, button }) => (
          <button
            type="button"
            className={cn(s.button, {
              [s.buttonActive]: tabKey === activeKey,
            })}
            key={tabKey}
            onClick={() => onChange(tabKey)}
            style={
              fullWidth
                ? {
                    minWidth: `${100 / items.length}%`,
                  }
                : undefined
            }
          >
            {button}
          </button>
        ))}
        <div className={s.scroller} ref={scrollerRef} />
      </div>
    </div>
  );
};
